// r24 Wave 9 — Visa A5 product page renderer.
// Composes hero + trust strip + long desc + bullets + best-for + documents + process + FAQ + related.
// Consumed by /visa/products/$slug (TH) and /en/visa/products/$slug (EN).

import ProductDepthBlock from "@/components/ProductDepthBlock";
import { Link } from "@tanstack/react-router";
import { Check, Clock, ShieldCheck, Plane, ArrowRight, FileCheck2 } from "lucide-react";
import type { VisaProduct } from "@/data/visa-products";
import { VISA_LEAD_ADVISOR, relatedVisaProducts } from "@/data/visa-products";
import { BRAND_PHONE, BRAND_EMAIL } from "@/lib/brand";
import { CrossSellStrip } from "@/components/CrossSellStrip";
import ConsultantAdvisoryBlock from "@/components/ConsultantAdvisoryBlock";

interface Props {
  product: VisaProduct;
  locale: "th" | "en";
  faqs: { q: string; a: string }[];
}

export default function VisaProductPage({ product, locale, faqs }: Props) {
  const isTh = locale === "th";
  const name = isTh ? product.nameTh : product.nameEn;
  const catLabel = isTh ? product.categoryLabelTh : product.categoryLabelEn;
  const longDesc = isTh ? product.longDescTh : product.longDescEn;
  const bullets = isTh ? product.bulletTh : product.bulletEn;
  const steps = isTh ? product.processStepsTh : product.processStepsEn;
  const related = relatedVisaProducts(product.slug, 6);
  const catalogHref = isTh ? "/visa/products" : "/en/visa/products";

  const L = isTh ? {
    trust: `ที่ปรึกษาโดย ${VISA_LEAD_ADVISOR} — BOI / ตม. / กรมการกงสุล`,
    priceLabel: "ค่าบริการวิชาชีพ",
    govLabel: "ค่าธรรมเนียมราชการ",
    turnLabel: "ระยะเวลาดำเนินการ",
    bestForLabel: "เหมาะกับ",
    docsLabel: "เอกสารหลัก",
    included: "รวมในบริการ",
    howTitle: "ขั้นตอนการทำงาน 4 ขั้น",
    faqTitle: "คำถามที่พบบ่อย",
    relatedTitle: "วีซ่าที่เกี่ยวข้อง",
    breadcrumb: ["หน้าหลัก", "วีซ่า", "สินค้าและบริการ"],
    ctaPrimary: "โทรปรึกษาฟรี",
    ctaLine: "แชท LINE",
    ctaEmail: "ส่งอีเมล",
    seeCatalog: "ดูวีซ่าทุกประเภท",
  } : {
    trust: `Advised by ${VISA_LEAD_ADVISOR} — BOI / Immigration / MFA-liaison`,
    priceLabel: "Professional fee",
    govLabel: "Government fee",
    turnLabel: "Processing time",
    bestForLabel: "Best for",
    docsLabel: "Core documents",
    included: "Included in service",
    howTitle: "Our 4-step process",
    faqTitle: "Frequently asked questions",
    relatedTitle: "Related visas",
    breadcrumb: ["Home", "Visa", "Products"],
    ctaPrimary: "Call for a free consult",
    ctaLine: "Chat on LINE",
    ctaEmail: "Email us",
    seeCatalog: "Browse full catalog",
  };

  return (
    <main className="min-h-screen bg-background">
      <nav className="mx-auto max-w-6xl px-4 pt-6 text-xs text-slate-500">
        <Link to={isTh ? "/" : "/en"} className="hover:text-indigo-700">{L.breadcrumb[0]}</Link>
        <span className="mx-1">/</span>
        <Link to={isTh ? "/visa" : "/en/visa"} className="hover:text-indigo-700">{L.breadcrumb[1]}</Link>
        <span className="mx-1">/</span>
        <Link to={catalogHref} className="hover:text-indigo-700">{L.breadcrumb[2]}</Link>
        <span className="mx-1">/</span>
        <span className="text-slate-700">{name}</span>
      </nav>

      <section className="bg-gradient-to-b from-sky-100/60 via-white to-background px-4 pt-6 pb-10">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-sky-500/15 px-3 py-1 font-bold text-sky-700">{catLabel}</span>
            <span className="rounded-full bg-slate-100 px-3 py-1 font-mono text-slate-600">SKU {product.sku}</span>
            <span className="rounded-full bg-emerald-500/15 px-3 py-1 font-semibold text-emerald-700">{product.visa.code}</span>
          </div>
          <h1 className="mt-3 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl">{name}</h1>
          <p data-speakable className="direct-answer mt-4 max-w-3xl rounded-xl border-l-4 border-sky-400 bg-sky-50/60 p-4 text-[15px] leading-relaxed text-slate-700">
            {isTh ? product.shortDescTh : product.shortDescEn} · {product.visa.tagline}
          </p>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500"><FileCheck2 className="h-4 w-4" /> {L.priceLabel}</div>
              <div className="price-band mt-1 text-lg font-extrabold text-emerald-700">
                THB {product.priceMin.toLocaleString()}–{product.priceMax.toLocaleString()}
              </div>
              <div className="mt-1 text-xs text-slate-500">{L.govLabel}: {product.govFee}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500"><Clock className="h-4 w-4" /> {L.turnLabel}</div>
              <div className="mt-1 text-lg font-extrabold text-navy-deep">{product.turnaround}</div>
              <div className="mt-1 text-xs text-slate-500">{product.visa.durationInitial} → {product.visa.durationMax}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4" /> E-E-A-T</div>
              <div className="mt-1 text-sm font-semibold text-navy-deep">{L.trust}</div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <a href={`tel:${BRAND_PHONE.replace(/[^+\d]/g, "")}`}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white hover:bg-emerald-700">
              <Plane className="h-4 w-4" /> {L.ctaPrimary}
            </a>
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noopener"
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-600 px-5 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-50">
              {L.ctaLine}
            </a>
            <a href={`mailto:${BRAND_EMAIL}?subject=${encodeURIComponent(product.sku)}`}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50">
              {L.ctaEmail}
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10">
        <div className="grid gap-8 lg:grid-cols-3">
          <article className="lg:col-span-2 space-y-6">
            <p className="text-[15px] leading-8 text-slate-700">{longDesc}</p>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="font-display text-lg font-extrabold text-navy-deep">{L.included}</h2>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {b}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="font-display text-lg font-extrabold text-navy-deep">{L.howTitle}</h2>
              <ol className="mt-3 space-y-3">
                {steps.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-bold text-sky-700">{i + 1}</span>
                    <p className="text-sm leading-6 text-slate-700">{s}</p>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="font-display text-lg font-extrabold text-navy-deep">{L.faqTitle}</h2>
              <div className="mt-3 space-y-3">
                {faqs.map((f, i) => (
                  <details key={i} className="rounded-lg border border-slate-100 p-3 open:bg-slate-50">
                    <summary className="cursor-pointer text-sm font-semibold text-navy-deep">{f.q}</summary>
                    <p className="mt-2 text-sm leading-6 text-slate-700">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </article>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500">{L.bestForLabel}</h3>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {product.bestFor.map((b, i) => <li key={i}>• {b}</li>)}
              </ul>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500">{L.docsLabel}</h3>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {product.documents.map((d, i) => <li key={i}>• {d}</li>)}
              </ul>
            </div>
            <Link to={catalogHref} className="block rounded-2xl border-2 border-sky-200 bg-sky-50 p-5 text-center hover:border-sky-400">
              <div className="text-xs uppercase tracking-widest text-sky-700">{L.seeCatalog}</div>
              <div className="mt-1 flex items-center justify-center gap-1 font-display font-extrabold text-navy-deep">
                {catalogHref} <ArrowRight className="h-4 w-4" />
              </div>
            </Link>
          </aside>
        </div>
      </section>

      <CrossSellStrip source="visa" locale={locale} />

      <section className="mx-auto max-w-6xl px-4 pb-16">
        <h2 className="font-display text-xl font-extrabold text-navy-deep">{L.relatedTitle}</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((r) => (
            <Link key={r.sku} to={isTh ? "/visa/products/$slug" : "/en/visa/products/$slug"} params={{ slug: r.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-sky-400 hover:shadow-md">
              <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700">{r.visa.code}</div>
              <div className="mt-1 font-display font-extrabold text-navy-deep group-hover:text-sky-700">{isTh ? r.nameTh : r.nameEn}</div>
              <div className="mt-2 text-xs text-slate-600 line-clamp-2">{isTh ? r.shortDescTh : r.shortDescEn}</div>
              <div className="mt-3 text-xs font-semibold text-emerald-700">THB {r.priceMin.toLocaleString()}–{r.priceMax.toLocaleString()} · {r.turnaround}</div>
            </Link>
          ))}
        </div>
      </section>
      <ProductDepthBlock cluster="visa" locale={locale === "th" ? "th" : "en"} />
      <ConsultantAdvisoryBlock locale={locale === "th" ? "th" : "en"} topic="Visa" />
    </main>
  );
}
